/**
 *
 * Blueprint Cards
 *
 */

import React, { memo, useEffect } from 'react';

import { SimpleGrid, Stack, Box, HStack } from '@chakra-ui/react';
import map from 'lodash/map';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { customTheme } from 'theme';

import { Card, PageHeaderWithIcon } from 'components/DataDisplay';
import { LearnMore } from 'components/DataDisplay/KnowledgeBase/Components/LearnMore';
import { useKnowledgeBase } from 'components/DataDisplay/KnowledgeBase/hook/useKnowledgeBase';
import { WithSpinner } from 'components/DataDisplay/Spinner/WithSpinner';
import { AddButton } from 'components/DataEntry';
import { LearnMoreButton } from 'components/DataEntry/Button/LearnMoreButton';
import { IconTypes } from 'components/Icons';
import { usePageContentContext } from 'components/Layout';
import { policyHubTourSteps, Tour } from 'components/Tour';
import { useBlueprintsContext } from 'containers/PolicyHub/Blueprints/hooks/context';
import { selectBlueprints } from 'containers/PolicyHub/Blueprints/selectors';
import { useAppColorMode } from 'hooks/useAppColorMode';

import { getBlueprintIcon } from '../../utils';
import { actions } from '../slice';

import { BlueprintCard } from './BlueprintCard';

export const BlueprintCards = memo(() => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { blueprints } = useSelector(selectBlueprints);

  const { colorMode } = useAppColorMode();
  const { contentHeight } = usePageContentContext();
  const { setShowPolicies, setShowExceptions } = useBlueprintsContext();
  const { setLearnMore } = useKnowledgeBase();

  useEffect(() => {
    dispatch(actions.getBlueprints({ q: {} }));
  }, [dispatch]);

  useEffect(() => {
    setShowPolicies(false);
    setShowExceptions(false);
  }, [setShowPolicies, setShowExceptions]);

  return (
    <Tour fieldName={'policy-hub'} steps={policyHubTourSteps}>
      <Card
        styles={{ card: { w: 'full', h: contentHeight - 8 + 'px', flex: 1 } }}
        contentComponent={
          <Stack w={'full'} spacing={5}>
            <HStack w="full" justify="space-between">
              <Box>
                <PageHeaderWithIcon
                  label="Policy Hub"
                  iconType={IconTypes.Policy}
                />
              </Box>

              <HStack>
                <LearnMoreButton
                  onOpen={() => setLearnMore('blueprints')}
                  label="Blueprints"
                />
                <AddButton
                  onClick={() => navigate('/policy-hub/blueprint/add')}
                  data-tour="4"
                  label="Add Blueprint"
                />
              </HStack>
            </HStack>
            <Box flex={1} overflow="auto">
              <WithSpinner
                loadStatus={{
                  loading: blueprints.isLoading,
                  loaded: blueprints.isSuccess,
                }}
                spinnerSize="xl"
              >
                <SimpleGrid
                  columns={[1, 2, 2, 4]}
                  spacing={customTheme.sizes.interCardSpacing}
                  className="policy_blueprints"
                  gridAutoRows="max-content"
                >
                  {map(blueprints.data, (each, index) => (
                    <BlueprintCard
                      data={each}
                      icon={getBlueprintIcon(
                        each.created_by !== 'system',
                        each.compliance ?? each.name,
                        colorMode,
                      )}
                      title={each.name}
                      description={each.description}
                      key={`${index}-card`}
                      onClick={record => {
                        //dispatch(actions.setCurrentPolicy(record));
                        // @ts-ignore
                        navigate(`/policy-hub/blueprint/${record?.id}`);
                      }}
                    />
                  ))}
                </SimpleGrid>
              </WithSpinner>
            </Box>
            <LearnMore />
          </Stack>
        }
      ></Card>
    </Tour>
  );
});
